import React, { useCallback } from 'react';
import Paper from '@mui/material/Paper';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import { useMemo, useState } from 'react';
import LicensePlate from '../LicensePlate';
import Filters from './Filters';
import DirectionsCarIcon from '@mui/icons-material/DirectionsCar';
import TwoWheelerIcon from '@mui/icons-material/TwoWheeler';
import { Box, CircularProgress, FormControl, IconButton, InputLabel, MenuItem, Pagination, Select } from '@mui/material';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import VisibilityIcon from '@mui/icons-material/Visibility';
import { useTranslation } from 'react-i18next';
import useToast, { ToastPosition, ToastType } from '../../hooks/useToast';
import DeleteIcon from '@mui/icons-material/Delete';
import ErrorIcon from '@mui/icons-material/Error';
import NotificationAddIcon from '@mui/icons-material/NotificationAdd';
import NotificationImportantIcon from '@mui/icons-material/NotificationImportant';
import ViewNumberPlateDetailsDialog from './ViewNumberPlateDetailsDialog';
import useMediaQuery from '@mui/material/useMediaQuery';
import { useTheme } from '@mui/material/styles';
import ActionButtons from './ActionButtons';
import MarkIrrelevantSuccess from './MarkIrrelevantSuccess';
import AlertDialog from '../AlertDialog/AlertDialog';
import { useEntries } from '../../context/EntriesContext';
import { Entry } from '../../types/Entries';
import useEntriesService from '../../services/useEntriesService';
import IrrelevantBadge from '../IrrelevantBadge/IrrelevantBadge';
import EmptyState from '../EmptyState';
import useCanAccess from '../../hooks/useCanAccess';
import MarkIrrelevantDialog from './MarkIrrelevantDialog';

interface Column {
    id: 'ismotorbike' | 'timestamp' | 'licenseplate' | 'carbrand' | 'carcolor' | 'imageVehicle' | 'actions';
    label: string;
    minWidth?: number;
    width?: number;
    align?: 'right';
    format?: (value: number) => string;
    getContent?: (row: Entry) => React.ReactNode;
}

// todo change this component name
export default function TableView() {
    const { t } = useTranslation("common");
    const { entriesData: { entries, totalCount }, options, setOptions, showImages, loading } = useEntries();
    const { pageIndex, pageSize } = options;
    const [anchorElMap, setAnchorElMap] = useState<{ [key: string]: HTMLElement | null }>({});
    const { showToast } = useToast();
    const { markCarAsIrrelevant, deleteEntry } = useEntriesService();
    const [createAlertLicensePlate, setCreateAlertLicensePlate] = useState<string | null>(null);
    const [selectedLicensePlate, setSelectedLicensePlate] = useState<string | null>(null);
    const [markIrrelevantDialogInfo, setMarkIrrelevantDialogInfo] = useState<{ open: boolean; licensePlate: string | null }>({ open: false, licensePlate: null });
    const theme = useTheme();
    const { canAccessAction } = useCanAccess();
    // todo move to utils
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    const isTablet = useMediaQuery(theme.breakpoints.between('sm', 'md'));

    const handleActionsOpen = (event: React.MouseEvent<HTMLButtonElement>, row: Entry) => {
        setAnchorElMap(prev => ({
            ...prev,
            [row.id]: event.currentTarget
        }));
    };

    const handleActionsClose = (rowId: string) => {
        setAnchorElMap(prev => ({
            ...prev,
            [rowId]: null
        }));
    };

    const handleMarkIrrelevant = useCallback((licensePlate: string, isIrrelevant: boolean) => {
        // Only show dialog when marking as irrelevant (not when unmarking)
        if (!isIrrelevant) {
            setMarkIrrelevantDialogInfo({ open: true, licensePlate });
        } else {
            // When unmarking as irrelevant, don't need reason
            showToast({
                message: () => <MarkIrrelevantSuccess licensePlate={licensePlate} markedAsIrrelevant={false} />,
                type: ToastType.PROMISE,
                promise: markCarAsIrrelevant(licensePlate, false),
                duration: 10000,
            });
        }
    }, [showToast, markCarAsIrrelevant]);

    const handleMarkIrrelevantConfirm = useCallback(async (reason: string) => {
        if (!markIrrelevantDialogInfo.licensePlate) return;
        
        showToast({
            message: () => <MarkIrrelevantSuccess licensePlate={markIrrelevantDialogInfo.licensePlate!} markedAsIrrelevant={false} />,
            type: ToastType.PROMISE,
            promise: markCarAsIrrelevant(markIrrelevantDialogInfo.licensePlate, true, reason),
            duration: 10000,
        });
        
        setMarkIrrelevantDialogInfo({ open: false, licensePlate: null });
    }, [markIrrelevantDialogInfo.licensePlate, showToast, markCarAsIrrelevant]);

    const handleDeleteEntry = useCallback(async (entryId: string) => {
        showToast({
            message: t("search.entryDeletedSuccessfully"),
            confirmMessage: t("search.deleteEntryConfirmation"),
            type: ToastType.CONFIRM,
            confirmCallback: async () => {
                try {
                    await deleteEntry(entryId);
                    showToast({
                        message: t("search.entryDeletedSuccessfully"),
                        type: ToastType.SUCCESS,
                        position: ToastPosition.TOP_CENTER,
                    });
                } catch (error) {
                    showToast({
                        message: t("search.entryDeletionFailed"),
                        type: ToastType.ERROR,
                        position: ToastPosition.TOP_CENTER,
                    });
                }
            }
        });
    }, [showToast, t, deleteEntry]);

    const getActions = useCallback((entry: Entry) => {
        const buttons = [];
        if (canAccessAction('markIrrelevant')) {
            buttons.push({
                key: 'mark-irrelevant',
                icon: entry.isirrelevant ? <NotificationImportantIcon fontSize='small' /> : <ErrorIcon fontSize='small' />,
                label: entry.isirrelevant ? t("search.markAsRelevant") : t("search.markAsIrrelevant"),
                onClick: () => handleMarkIrrelevant(entry.licenseplate, entry.isirrelevant),
            });
        }
        if (canAccessAction('createAlert')) {
            buttons.push({
                key: 'create-alert',
                icon: <NotificationAddIcon fontSize='small' />,
                label: t("search.createAlert"),
                onClick: () => {
                    setCreateAlertLicensePlate(entry.licenseplate);
                },
            });
        };
        if (canAccessAction('deleteEntry')) {
            buttons.push({
                key: 'delete',
                icon: <DeleteIcon fontSize='small' />,
                label: t("search.delete"),
                onClick: () => handleDeleteEntry(entry.id),
            });
        }

        return buttons;
    }, [handleDeleteEntry, handleMarkIrrelevant, t, canAccessAction]);

    const columns = useMemo(() => {
        const initialColumns: Column[] = [
            {
                id: "ismotorbike",
                label: '',
                getContent: ({ ismotorbike }) => {
                    return ismotorbike ? <TwoWheelerIcon /> : <DirectionsCarIcon />;
                },
            },
            { id: 'timestamp', label: t("search.dateAndTime") },
            {
                id: 'licenseplate',
                label: t("search.licensePlate"),
                getContent: ({ licenseplate, isirrelevant }) => (
                    <Box sx={{ position: 'relative', display: 'inline-block' }}>
                        <LicensePlate licenseNumber={licenseplate} />
                        {isirrelevant && <IrrelevantBadge />}
                    </Box>
                )
            },
        ];

        if (showImages) {
            initialColumns.push({
                id: 'imageVehicle',
                label: t("search.image"),
                align: 'right',
            });
        }

        initialColumns.push({
            id: 'actions',
            label: `${!isMobile ? t("search.filters.results", { nr: totalCount }) : ''}`,
            width: 160,
            getContent: (row) => {
                const { licenseplate } = row;
                return (
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <IconButton onClick={() => {
                            setSelectedLicensePlate(licenseplate);
                        }}>
                            <VisibilityIcon />
                        </IconButton>
                        <IconButton onClick={event => { handleActionsOpen(event, row) }}>
                            <MoreVertIcon />
                        </IconButton>
                        <ActionButtons
                            isPopoverOpen={Boolean(anchorElMap[row.id])}
                            anchorEl={anchorElMap[row.id]}
                            onPopoverClose={() => handleActionsClose(row.id)}
                            buttons={getActions(row)}
                        />
                    </Box>
                );
            }
        });

        return initialColumns;
    }, [anchorElMap, getActions, isMobile, showImages, t, totalCount]);

    return (
        <Paper sx={{ width: '100%', height: '100%', overflow: 'hidden' }}>
            <Filters />
            {entries.length === 0 && !loading ? (
                <EmptyState
                    title={t("search.noEntriesFound")}
                    description={t("search.tryAdjustingFilters")}
                    showParkingAnimation
                />
            ) : null}
            <TableContainer
                component={Paper}
                sx={{
                    maxHeight: `calc(100% - ${isMobile ? 230 : isTablet ? 185 : 130}px)`,
                    height: '100%',
                }}
            >
                {
                    loading ?
                        <Box sx={{ width: '100%', height: '100%', display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                            <CircularProgress />
                        </Box> : (
                            <Table stickyHeader aria-label="sticky table">
                                <TableHead>
                                    <TableRow>
                                        {columns.map((column) => (
                                            <TableCell
                                                key={column.id}
                                                align={column.align}
                                                style={{
                                                    backgroundColor: 'var(--template-palette-AppBar-darkBg, var(--AppBar-background))',
                                                    minWidth: column.minWidth,
                                                    width: column.width,
                                                    fontSize: (isMobile || isTablet) ? '0.875rem' : '1rem',
                                                    fontWeight: '600',
                                                }}
                                            >
                                                {column.label}
                                            </TableCell>
                                        ))}
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {entries
                                        .map((row, index) => {
                                            return (
                                                <TableRow
                                                    hover
                                                    role="checkbox"
                                                    tabIndex={-1}
                                                    key={row.id}
                                                    sx={{
                                                        backgroundColor: index % 2 === 0 ? 'rgba(255, 255, 255, 0.05)' : 'inherit',
                                                        '&:hover': {
                                                            backgroundColor: 'rgba(255, 255, 255, 0.1) !important',
                                                        },
                                                    }}
                                                >
                                                    {columns.map((column) => {
                                                        // @ts-ignore
                                                        // todo fix types
                                                        const value = row[column.id];

                                                        if (column.getContent) {
                                                            return (
                                                                <TableCell key={column.id} align={column.align} sx={{ fontSize: '1rem', fontWeight: '600' }}>
                                                                    {column.getContent(row)}
                                                                </TableCell>
                                                            );
                                                        }

                                                        // todo getcontent
                                                        if (column.id === 'imageVehicle') {
                                                            return (
                                                                <TableCell key={column.id} align={column.align}>
                                                                    {(row.imageLicensePlate || row.imageVehicle) && (
                                                                        <img
                                                                            src={row.imageLicensePlate}
                                                                            alt={''}
                                                                            style={{ maxWidth: '100px', maxHeight: '100px' }}
                                                                            onError={(e) => {
                                                                                if (row.imageVehicle && e.currentTarget.src !== row.imageVehicle) {
                                                                                    e.currentTarget.src = row.imageVehicle;
                                                                                } else {
                                                                                    e.currentTarget.style.display = "none";
                                                                                }
                                                                            }}
                                                                        />
                                                                    )}
                                                                </TableCell>
                                                            )
                                                        }
                                                        return (
                                                            <TableCell
                                                                key={column.id}
                                                                align={column.align}
                                                                sx={{ fontSize: '1rem', fontWeight: '600' }}
                                                            >
                                                                {column.format && typeof value === 'number'
                                                                    ? column.format(value)
                                                                    : value}
                                                            </TableCell>
                                                        );
                                                    })}
                                                </TableRow>
                                            );
                                        })}
                                </TableBody>
                            </Table>
                        )
                }
            </TableContainer>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', padding: '10px' }}>
                <FormControl sx={{ minWidth: 120, maxHeight: '5px' }}>
                    <InputLabel>{t("search.rowsPerPage")}</InputLabel>
                    <Select
                        labelId="rows-per-page"
                        id="rows-per-page"
                        value={pageSize}
                        label={t("search.rowsPerPage")}
                        onChange={event => setOptions({
                            ...options,
                            pageSize: +event.target.value,
                            pageIndex: 0,
                        })}
                    >
                        <MenuItem value={10}>10</MenuItem>
                        <MenuItem value={100}>100</MenuItem>
                        <MenuItem value={500}>500</MenuItem>
                    </Select>
                </FormControl>
                <Pagination
                    count={Math.ceil(totalCount / pageSize)}
                    page={pageIndex + 1}
                    onChange={(_e, pageIndex) => setOptions({ ...options, pageIndex: pageIndex - 1 })}
                    showFirstButton
                    showLastButton
                    sx={{ padding: '10px 0', display: 'flex', justifyContent: 'center' }}
                />
            </Box>
            {!!selectedLicensePlate && (
                <ViewNumberPlateDetailsDialog
                    licensePlate={selectedLicensePlate}
                    open={!!selectedLicensePlate}
                    onClose={() => {
                        setSelectedLicensePlate(null);
                    }}
                />
            )}
            {!!createAlertLicensePlate && (
                <AlertDialog
                    open={!!createAlertLicensePlate}
                    onClose={() => {
                        setCreateAlertLicensePlate('');
                    }}
                    initialValues={{
                        numberPlate: createAlertLicensePlate,
                    }}
                />
            )}
            <MarkIrrelevantDialog
                open={markIrrelevantDialogInfo.open}
                licensePlate={markIrrelevantDialogInfo.licensePlate}
                onConfirm={handleMarkIrrelevantConfirm}
                onCancel={() => {
                    setMarkIrrelevantDialogInfo({ open: false, licensePlate: null });
                }}
            />
        </Paper>
    );
};
